import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getSocialAccounts, getSocialAccountsStats, getSocialAccountDetail,
  getTikTokAccounts, getTikTokAccountsStats, getTikTokAccountDetail,
  getFacebookAccounts, getFacebookAccountsStats, getFacebookAccountDetail,
  getInstagramAccountTokens, getTikTokAccountTokens, getFacebookAccountTokens,
  getUserSocialScopes,
} from '../../services/adminApi';
import ScopesPanel from '../components/ScopesPanel';
import './SocialAccounts.css';

// Map the page-level _platform value (used in the URL/tab) to the key
// returned by /admin/users/{id}/social-scopes — they differ for Facebook.
const SCOPES_PLATFORM_KEY = {
  instagram: 'instagram',
  facebook: 'facebook_page',
  tiktok: 'tiktok',
};

const CONNECTION_FILTERS = [
  { value: '', labelKey: 'admin.socialAccounts.filterAll' },
  { value: 'true', labelKey: 'admin.socialAccounts.filterConnected' },
  { value: 'false', labelKey: 'admin.socialAccounts.filterExpired' },
];

function SocialAccounts() {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const isSuperadmin = currentUser?.role === 'superadmin';

  // Platform tab
  const [platform, setPlatform] = useState('instagram');

  // Data
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [tiktokStats, setTiktokStats] = useState(null);
  const [facebookStats, setFacebookStats] = useState(null);
  const [total, setTotal] = useState(0);

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [connectionFilter, setConnectionFilter] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Token reveal (superadmin only)
  const [tokens, setTokens] = useState(null);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [tokensError, setTokensError] = useState(null);
  const [tokensVisible, setTokensVisible] = useState({}); // {access_token: true, ...}

  // OAuth granted-scopes inspection (superadmin only). Reuses the
  // user-level endpoint /admin/users/{user_id}/social-scopes and picks
  // the platform key matching the currently selected account.
  const [scopesInfo, setScopesInfo] = useState(null);   // single platform info { connected, scopes, missing_scopes, ... }
  const [scopesLoading, setScopesLoading] = useState(false);
  const [scopesError, setScopesError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const [igRes, ttRes, fbRes] = await Promise.all([
        getSocialAccountsStats(),
        getTikTokAccountsStats(),
        getFacebookAccountsStats(),
      ]);
      setStats(igRes.data);
      setTiktokStats(ttRes.data);
      setFacebookStats(fbRes.data);
    } catch (err) {
      console.error('Failed to load social stats:', err);
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const isConnected = connectionFilter === '' ? null : connectionFilter === 'true';
      const fetchers = { instagram: getSocialAccounts, tiktok: getTikTokAccounts, facebook: getFacebookAccounts };
      const fetcher = fetchers[platform] || getSocialAccounts;
      const res = await fetcher(page, pageSize, search || null, isConnected);
      setAccounts(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load social accounts:', err);
      setError(t('admin.socialAccounts.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, connectionFilter, platform, t]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleConnectionFilterChange = (e) => {
    setConnectionFilter(e.target.value);
    setPage(1);
  };

  const openDetail = async (accountId) => {
    setDetailLoading(true);
    try {
      const detailFetchers = { instagram: getSocialAccountDetail, tiktok: getTikTokAccountDetail, facebook: getFacebookAccountDetail };
      const fetcher = detailFetchers[platform] || getSocialAccountDetail;
      const res = await fetcher(accountId);
      setSelectedAccount({ ...res.data, _platform: platform });
    } catch (err) {
      console.error('Failed to load account detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedAccount(null);
    setTokens(null);
    setTokensError(null);
    setTokensVisible({});
    setScopesInfo(null);
    setScopesError(null);
  };

  const handleRevealScopes = async () => {
    if (!selectedAccount || !selectedAccount.user_id) return;
    setScopesLoading(true);
    setScopesError(null);
    try {
      const res = await getUserSocialScopes(selectedAccount.user_id);
      const key = SCOPES_PLATFORM_KEY[selectedAccount._platform];
      const info = res.data?.platforms?.[key] || null;
      if (!info) {
        setScopesError(t('admin.socialAccounts.scopesNotFound', 'No scope data for this platform'));
      } else {
        setScopesInfo(info);
      }
    } catch (err) {
      console.error('Failed to load OAuth scopes:', err);
      setScopesError(
        err.response?.status === 403
          ? t('admin.socialAccounts.tokensForbidden')
          : t('admin.socialAccounts.scopesError', 'Failed to load granted permissions')
      );
    } finally {
      setScopesLoading(false);
    }
  };

  const handleRevealTokens = async () => {
    if (!selectedAccount) return;
    setTokensLoading(true);
    setTokensError(null);
    try {
      const fetchers = {
        instagram: getInstagramAccountTokens,
        tiktok: getTikTokAccountTokens,
        facebook: getFacebookAccountTokens,
      };
      const fetcher = fetchers[selectedAccount._platform];
      const res = await fetcher(selectedAccount.id);
      setTokens(res.data);
    } catch (err) {
      console.error('Failed to load tokens:', err);
      setTokensError(
        err.response?.status === 403
          ? t('admin.socialAccounts.tokensForbidden')
          : t('admin.socialAccounts.tokensError')
      );
    } finally {
      setTokensLoading(false);
    }
  };

  const toggleTokenVisible = (key) => {
    setTokensVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const copyToClipboard = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error('Clipboard write failed:', err);
    }
  };

  const handlePlatformChange = (newPlatform) => {
    if (newPlatform !== platform) {
      setPlatform(newPlatform);
      setPage(1);
      setSearch('');
      setConnectionFilter('');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const statsMap = { instagram: stats, tiktok: tiktokStats, facebook: facebookStats };
  const activeStats = statsMap[platform] || stats;

  const renderConnectionBadge = (account) => {
    if (account.is_token_valid) {
      return <span className="sa-badge sa-badge-connected">{t('admin.socialAccounts.connected')}</span>;
    }
    if (account.token_expires_at) {
      return <span className="sa-badge sa-badge-expired">{t('admin.socialAccounts.expired')}</span>;
    }
    return <span className="sa-badge sa-badge-notoken">{t('admin.socialAccounts.noToken')}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="social-accounts-page">
      <div className="sa-header">
        <h1>{t('admin.socialAccounts.title')}</h1>
        <button className="sa-btn-refresh" onClick={() => { fetchStats(); fetchAccounts(); }}>
          ↻ {t('admin.socialAccounts.refresh')}
        </button>
      </div>

      {/* Platform tabs */}
      <div className="sa-platform-tabs">
        <button
          className={`sa-platform-tab ${platform === 'instagram' ? 'sa-platform-tab-active' : ''}`}
          onClick={() => handlePlatformChange('instagram')}
        >
          📸 Instagram
          {stats && <span className="sa-tab-count">{stats.total_accounts}</span>}
        </button>
        <button
          className={`sa-platform-tab ${platform === 'tiktok' ? 'sa-platform-tab-active' : ''}`}
          onClick={() => handlePlatformChange('tiktok')}
        >
          🎵 TikTok
          {tiktokStats && <span className="sa-tab-count">{tiktokStats.total_accounts}</span>}
        </button>
        <button
          className={`sa-platform-tab ${platform === 'facebook' ? 'sa-platform-tab-active' : ''}`}
          onClick={() => handlePlatformChange('facebook')}
        >
          📘 Facebook
          {facebookStats && <span className="sa-tab-count">{facebookStats.total_accounts}</span>}
        </button>
      </div>

      {/* Stats cards */}
      {activeStats && (
        <div className="sa-stats-row">
          <div className="sa-stat-card">
            <span className="sa-stat-value">{activeStats.total_accounts}</span>
            <span className="sa-stat-label">{t('admin.socialAccounts.statTotal')}</span>
          </div>
          <div className="sa-stat-card sa-stat-active">
            <span className="sa-stat-value">{activeStats.active_accounts}</span>
            <span className="sa-stat-label">{t('admin.socialAccounts.statActive')}</span>
          </div>
          <div className="sa-stat-card sa-stat-expired">
            <span className="sa-stat-value">{activeStats.expired_tokens}</span>
            <span className="sa-stat-label">{t('admin.socialAccounts.statExpired')}</span>
          </div>
          <div className="sa-stat-card sa-stat-notoken">
            <span className="sa-stat-value">{activeStats.no_token}</span>
            <span className="sa-stat-label">{t('admin.socialAccounts.statNoToken')}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="sa-filters">
        <input
          type="text"
          className="sa-search-input"
          placeholder={t('admin.socialAccounts.searchPlaceholder')}
          value={search}
          onChange={handleSearchChange}
        />
        <select
          className="sa-select"
          value={connectionFilter}
          onChange={handleConnectionFilterChange}
        >
          {CONNECTION_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{t(f.labelKey)}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && <div className="sa-error">{error}</div>}

      {/* Table */}
      {loading ? (
        <div className="sa-loading">{t('common.loading')}</div>
      ) : accounts.length === 0 ? (
        <div className="sa-empty">{t('admin.socialAccounts.noAccounts')}</div>
      ) : (
        <div className="sa-table-container">
          <table className="sa-table" role="grid">
            <thead>
              <tr>
                <th>{t('admin.socialAccounts.colUser')}</th>
                <th>{platform === 'facebook' ? t('admin.socialAccounts.colPageName') : t('admin.socialAccounts.colUsername')}</th>
                <th>{platform === 'facebook' ? t('admin.socialAccounts.colCategory') : t('admin.socialAccounts.colName')}</th>
                <th>{platform === 'facebook' ? t('admin.socialAccounts.colFans') : t('admin.socialAccounts.colFollowers')}</th>
                {platform === 'tiktok' && <th>{t('admin.socialAccounts.colVideos')}</th>}
                <th>{t('admin.socialAccounts.colStatus')}</th>
                <th>{platform === 'facebook' ? t('admin.socialAccounts.colConnectedSince') : t('admin.socialAccounts.colTokenExpires')}</th>
                <th>{platform === 'facebook' ? t('admin.socialAccounts.lastPost') : t('admin.socialAccounts.colConnectedSince')}</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr
                  key={account.id}
                  className="sa-row-clickable"
                  onClick={() => openDetail(account.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') openDetail(account.id); }}
                >
                  <td>
                    <span className="sa-user-email">{account.user_email || '—'}</span>
                  </td>
                  <td>
                    <div className="sa-username-cell">
                      {(account.profile_picture_url || account.avatar_url || account.page_picture_url) ? (
                        <img
                          src={account.profile_picture_url || account.avatar_url || account.page_picture_url}
                          alt=""
                          className="sa-profile-pic"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && e.target.nextElementSibling.classList.remove('sa-hidden'); }}
                        />
                      ) : null}
                      <span className={`sa-avatar-fallback ${(account.profile_picture_url || account.avatar_url || account.page_picture_url) ? 'sa-hidden' : ''}`}>
                        {(account.display_name || account.instagram_username || account.page_name || '?')[0].toUpperCase()}
                      </span>
                      <span>
                        {platform === 'tiktok'
                          ? (account.display_name || account.open_id)
                          : platform === 'facebook'
                            ? (account.page_name || account.page_id)
                            : `@${account.instagram_username}`}
                      </span>
                    </div>
                  </td>
                  <td>
                    {platform === 'tiktok'
                      ? (account.display_name || '—')
                      : platform === 'facebook'
                        ? (account.page_category || '—')
                        : (account.instagram_name || '—')}
                  </td>
                  <td>
                    {platform === 'facebook'
                      ? (account.fan_count != null ? account.fan_count.toLocaleString() : '—')
                      : platform === 'tiktok'
                        ? (account.follower_count != null ? account.follower_count.toLocaleString() : '—')
                        : (account.followers_count != null ? account.followers_count.toLocaleString() : '—')}
                  </td>
                  {platform === 'tiktok' && (
                    <td>{account.video_count != null ? account.video_count.toLocaleString() : '—'}</td>
                  )}
                  <td>{renderConnectionBadge(account)}</td>
                  <td>{platform === 'facebook' ? formatDate(account.connected_at) : formatDate(account.token_expires_at)}</td>
                  <td>{platform === 'facebook' ? formatDate(account.last_post_at) : formatDate(account.connected_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="sa-pagination">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t('common.previous')}
          </button>
          <span className="sa-page-info">
            {page} {t('common.of')} {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {(selectedAccount || detailLoading) && (
        <div className="sa-modal-overlay" onClick={closeDetail}>
          <div className="sa-modal-card" onClick={(e) => e.stopPropagation()}>
            {detailLoading ? (
              <div className="sa-loading">{t('common.loading')}</div>
            ) : selectedAccount && (
              <>
                <div className="sa-modal-header">
                  <h2>
                    {selectedAccount._platform === 'tiktok' ? '🎵 ' : selectedAccount._platform === 'facebook' ? '📘 ' : '📸 '}
                    {t('admin.socialAccounts.detailTitle')}
                  </h2>
                  <button className="sa-modal-close" onClick={closeDetail}>✕</button>
                </div>
                <div className="sa-modal-body">
                  {/* Account header */}
                  <div className="sa-detail-row">
                    {(selectedAccount.profile_picture_url || selectedAccount.avatar_url || selectedAccount.page_picture_url) ? (
                      <img
                        src={selectedAccount.profile_picture_url || selectedAccount.avatar_url || selectedAccount.page_picture_url}
                        alt={selectedAccount.instagram_username || selectedAccount.display_name || selectedAccount.page_name}
                        className="sa-detail-avatar"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && e.target.nextElementSibling.classList.remove('sa-hidden'); }}
                      />
                    ) : null}
                    <span className={`sa-detail-avatar-fallback ${(selectedAccount.profile_picture_url || selectedAccount.avatar_url || selectedAccount.page_picture_url) ? 'sa-hidden' : ''}`}>
                      {(selectedAccount.display_name || selectedAccount.instagram_username || selectedAccount.page_name || '?')[0].toUpperCase()}
                    </span>
                    <div>
                      <h3>
                        {selectedAccount._platform === 'tiktok'
                          ? (selectedAccount.display_name || selectedAccount.open_id)
                          : selectedAccount._platform === 'facebook'
                            ? (selectedAccount.page_name || selectedAccount.page_id)
                            : `@${selectedAccount.instagram_username}`}
                      </h3>
                      <p className="sa-detail-name">
                        {selectedAccount._platform === 'tiktok'
                          ? selectedAccount.display_name
                          : selectedAccount._platform === 'facebook'
                            ? selectedAccount.page_category
                            : selectedAccount.instagram_name}
                      </p>
                    </div>
                  </div>

                  {/* Detail grid — Instagram */}
                  {selectedAccount._platform === 'instagram' && (
                    <div className="sa-detail-grid">
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colUser')}</label>
                        <span>{selectedAccount.user_email}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>IG User ID</label>
                        <span>{selectedAccount.instagram_user_id || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colFollowers')}</label>
                        <span>{selectedAccount.followers_count?.toLocaleString() || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.mediaCount')}</label>
                        <span>{selectedAccount.media_count?.toLocaleString() || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>Facebook Page</label>
                        <span>{selectedAccount.facebook_page_name || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colStatus')}</label>
                        {renderConnectionBadge(selectedAccount)}
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colTokenExpires')}</label>
                        <span>{formatDateTime(selectedAccount.token_expires_at)}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colConnectedSince')}</label>
                        <span>{formatDateTime(selectedAccount.connected_at)}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.lastPost')}</label>
                        <span>{formatDateTime(selectedAccount.last_post_at)}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.lastRefreshed')}</label>
                        <span>{formatDateTime(selectedAccount.last_refreshed_at)}</span>
                      </div>
                    </div>
                  )}

                  {/* Detail grid — TikTok */}
                  {selectedAccount._platform === 'tiktok' && (
                    <div className="sa-detail-grid">
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colUser')}</label>
                        <span>{selectedAccount.user_email}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>Open ID</label>
                        <span className="sa-mono">{selectedAccount.open_id || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colFollowers')}</label>
                        <span>{selectedAccount.follower_count?.toLocaleString() || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.tiktokFollowing')}</label>
                        <span>{selectedAccount.following_count?.toLocaleString() || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colVideos')}</label>
                        <span>{selectedAccount.video_count?.toLocaleString() || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.tiktokLikes')}</label>
                        <span>{selectedAccount.likes_count?.toLocaleString() || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colStatus')}</label>
                        {renderConnectionBadge(selectedAccount)}
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colTokenExpires')}</label>
                        <span>{formatDateTime(selectedAccount.token_expires_at)}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colConnectedSince')}</label>
                        <span>{formatDateTime(selectedAccount.connected_at)}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.lastPost')}</label>
                        <span>{formatDateTime(selectedAccount.last_post_at)}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.lastRefreshed')}</label>
                        <span>{formatDateTime(selectedAccount.last_refreshed_at)}</span>
                      </div>

                      {/* Business API info */}
                      <div className="sa-detail-item sa-detail-full-width sa-detail-section-header">
                        <label>{t('admin.socialAccounts.businessApiSection')}</label>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.businessTokenType')}</label>
                        <span>
                          {selectedAccount.business_token_type === 'tt_user'
                            ? <span className="sa-badge sa-badge-connected">TT User</span>
                            : selectedAccount.business_token_type === 'marketing'
                              ? <span className="sa-badge sa-badge-expired">Marketing</span>
                              : <span className="sa-badge sa-badge-notoken">{t('admin.socialAccounts.noToken')}</span>}
                        </span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.businessTokenValid')}</label>
                        <span>
                          {selectedAccount.is_business_token_valid
                            ? <span className="sa-badge sa-badge-connected">✓</span>
                            : <span className="sa-badge sa-badge-notoken">✗</span>}
                        </span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.publishCapable')}</label>
                        <span>
                          {selectedAccount.has_publish_capable_token
                            ? <span className="sa-badge sa-badge-connected">✓</span>
                            : <span className="sa-badge sa-badge-notoken">✗</span>}
                        </span>
                      </div>
                      {selectedAccount.business_id && (
                        <div className="sa-detail-item">
                          <label>Business ID</label>
                          <span className="sa-mono">{selectedAccount.business_id}</span>
                        </div>
                      )}
                      {selectedAccount.business_token_expires_at && (
                        <div className="sa-detail-item">
                          <label>{t('admin.socialAccounts.businessTokenExpires')}</label>
                          <span>{formatDateTime(selectedAccount.business_token_expires_at)}</span>
                        </div>
                      )}

                      {selectedAccount.profile_deep_link && (
                        <div className="sa-detail-item sa-detail-full-width">
                          <label>{t('admin.socialAccounts.tiktokProfile')}</label>
                          <a
                            href={selectedAccount.profile_deep_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sa-tiktok-link"
                          >
                            🎵 {t('admin.socialAccounts.viewOnTikTok')}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Detail grid — Facebook */}
                  {selectedAccount._platform === 'facebook' && (
                    <div className="sa-detail-grid">
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colUser')}</label>
                        <span>{selectedAccount.user_email}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>Page ID</label>
                        <span className="sa-mono">{selectedAccount.page_id || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colPageName')}</label>
                        <span>{selectedAccount.page_name || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colCategory')}</label>
                        <span>{selectedAccount.page_category || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colFollowers')}</label>
                        <span>{selectedAccount.followers_count?.toLocaleString() || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colFans')}</label>
                        <span>{selectedAccount.fan_count?.toLocaleString() || '—'}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colStatus')}</label>
                        {renderConnectionBadge(selectedAccount)}
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.colConnectedSince')}</label>
                        <span>{formatDateTime(selectedAccount.connected_at)}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.lastPost')}</label>
                        <span>{formatDateTime(selectedAccount.last_post_at)}</span>
                      </div>
                      <div className="sa-detail-item">
                        <label>{t('admin.socialAccounts.lastRefreshed')}</label>
                        <span>{formatDateTime(selectedAccount.last_refreshed_at)}</span>
                      </div>
                      {selectedAccount.page_url && (
                        <div className="sa-detail-item sa-detail-full-width">
                          <label>{t('admin.socialAccounts.facebookPageUrl')}</label>
                          <a
                            href={selectedAccount.page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sa-tiktok-link"
                          >
                            📘 {t('admin.socialAccounts.viewOnFacebook')}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tokens reveal — superadmin only */}
                  {isSuperadmin && (
                    <div className="sa-tokens-section">
                      <div className="sa-tokens-header">
                        <h4>🔐 {t('admin.socialAccounts.tokensTitle')}</h4>
                        {!tokens && !tokensLoading && (
                          <button
                            type="button"
                            className="sa-btn-reveal-tokens"
                            onClick={handleRevealTokens}
                          >
                            {t('admin.socialAccounts.tokensReveal')}
                          </button>
                        )}
                      </div>
                      <p className="sa-tokens-warning">
                        {t('admin.socialAccounts.tokensWarning')}
                      </p>
                      {tokensLoading && (
                        <div className="sa-loading">{t('common.loading')}</div>
                      )}
                      {tokensError && (
                        <div className="sa-error">{tokensError}</div>
                      )}
                      {tokens && (
                        <div className="sa-tokens-grid">
                          <TokenField
                            label={t('admin.socialAccounts.tokenAccess')}
                            value={tokens.access_token}
                            visible={tokensVisible.access_token}
                            onToggle={() => toggleTokenVisible('access_token')}
                            onCopy={copyToClipboard}
                            t={t}
                          />
                          {tokens.refresh_token && (
                            <TokenField
                              label={t('admin.socialAccounts.tokenRefresh')}
                              value={tokens.refresh_token}
                              visible={tokensVisible.refresh_token}
                              onToggle={() => toggleTokenVisible('refresh_token')}
                              onCopy={copyToClipboard}
                              t={t}
                            />
                          )}
                          <div className="sa-detail-item">
                            <label>{t('admin.socialAccounts.colTokenExpires')}</label>
                            <span>{formatDateTime(tokens.token_expires_at)}</span>
                          </div>
                          {tokens.refresh_token_expires_at && (
                            <div className="sa-detail-item">
                              <label>{t('admin.socialAccounts.tokenRefreshExpires')}</label>
                              <span>{formatDateTime(tokens.refresh_token_expires_at)}</span>
                            </div>
                          )}
                          {tokens.external_id && (
                            <div className="sa-detail-item">
                              <label>{t('admin.socialAccounts.tokenExternalId')}</label>
                              <span className="sa-mono">{tokens.external_id}</span>
                            </div>
                          )}
                          {tokens.extra && Object.entries(tokens.extra).map(([k, v]) => {
                            if (v == null) return null;
                            const isToken = /token/i.test(k);
                            return isToken ? (
                              <TokenField
                                key={k}
                                label={k}
                                value={String(v)}
                                visible={tokensVisible[k]}
                                onToggle={() => toggleTokenVisible(k)}
                                onCopy={copyToClipboard}
                                t={t}
                              />
                            ) : (
                              <div key={k} className="sa-detail-item">
                                <label>{k}</label>
                                <span className="sa-mono">{String(v)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* OAuth granted-scopes — superadmin only.
                      Reuses /admin/users/{user_id}/social-scopes and
                      picks the entry for the currently selected
                      platform. Helps spot publish-blocking scope
                      mismatches (e.g. missing instagram_content_publish). */}
                  {isSuperadmin && (
                    <div className="sa-tokens-section">
                      <div className="sa-tokens-header">
                        <h4>🔍 {t('admin.socialAccounts.scopesTitle', 'Granted permissions')}</h4>
                        {!scopesInfo && !scopesLoading && (
                          <button
                            type="button"
                            className="sa-btn-reveal-tokens"
                            onClick={handleRevealScopes}
                          >
                            {t('admin.socialAccounts.scopesReveal', 'Show granted permissions')}
                          </button>
                        )}
                        {scopesInfo && !scopesLoading && (
                          <button
                            type="button"
                            className="sa-btn-reveal-tokens"
                            onClick={handleRevealScopes}
                          >
                            ↻ {t('admin.socialAccounts.scopesRefresh', 'Refresh')}
                          </button>
                        )}
                      </div>
                      <p className="sa-tokens-warning">
                        {t(
                          'admin.socialAccounts.scopesHint',
                          "Read-only check — calls Meta debug_token (or reads stored TikTok scopes) to compare what was granted against what the publish pipeline expects.",
                        )}
                      </p>
                      {scopesLoading && (
                        <div className="sa-loading">{t('common.loading')}</div>
                      )}
                      {scopesError && (
                        <div className="sa-error">{scopesError}</div>
                      )}
                      {scopesInfo && (
                        <ScopesPanel
                          platform={SCOPES_PLATFORM_KEY[selectedAccount._platform]}
                          info={scopesInfo}
                        />
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TokenField({ label, value, visible, onToggle, onCopy, t }) {
  if (!value) return null;
  const masked = `${value.slice(0, 6)}…${value.slice(-4)} (${value.length} chars)`;
  return (
    <div className="sa-detail-item sa-detail-full-width sa-token-field">
      <label>{label}</label>
      <div className="sa-token-row">
        <code className="sa-token-value">{visible ? value : masked}</code>
        <button
          type="button"
          className="sa-token-btn"
          onClick={onToggle}
        >
          {visible ? t('admin.socialAccounts.tokenHide') : t('admin.socialAccounts.tokenShow')}
        </button>
        <button
          type="button"
          className="sa-token-btn"
          onClick={() => onCopy(value)}
        >
          {t('admin.socialAccounts.tokenCopy')}
        </button>
      </div>
    </div>
  );
}

export default SocialAccounts;
